import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { canUseEzeeAI } from "../../utils/ezeeAccess";
import MissionWorkspace from "./MissionWorkspace";

const DRAWER_WIDTH = 460;

function getInstanceUniqueId(pathname = "") {
  const match = String(pathname).match(/^\/instance\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function numericSegmentAfter(segments, key) {
  const index = segments.indexOf(key);
  if (index === -1 || index + 1 >= segments.length) return null;
  const value = Number(segments[index + 1]);
  return Number.isFinite(value) ? value : null;
}

function buildPageContext(pathname = "") {
  const segments = String(pathname).split("/").filter(Boolean);
  const route = pathname || "/";
  const moduleKey = segments[2] || null;

  const salesOrderIndex = segments.indexOf("sales-order");
  if (salesOrderIndex !== -1) {
    return {
      surface: "sales-order",
      module: "sales",
      route,
      orderId: numericSegmentAfter(segments, "orders"),
      salesOrderId: Number(segments[salesOrderIndex + 1]) || null,
    };
  }

  const invoiceIndex = segments.indexOf("invoice");
  if (invoiceIndex !== -1) {
    return {
      surface: "invoice",
      module: "finance",
      route,
      orderId: numericSegmentAfter(segments, "orders"),
      invoiceId: Number(segments[invoiceIndex + 1]) || null,
    };
  }

  const poIndex = segments.indexOf("purchase-order");
  if (poIndex !== -1) {
    return {
      surface: "purchase-order",
      module: "purchasing",
      route,
      purchaseOrderId: Number(segments[poIndex + 1]) || null,
      orderId: numericSegmentAfter(segments, "orders"),
    };
  }

  if (segments.includes("orders")) {
    const orderId = numericSegmentAfter(segments, "orders");
    return {
      surface: orderId ? "project" : "projects-list",
      module: "sales",
      route,
      ...(orderId ? { projectId: orderId, orderId } : {}),
    };
  }

  if (segments.includes("customers")) {
    return {
      surface: numericSegmentAfter(segments, "customers") ? "customer" : "customers-list",
      module: "sales",
      route,
      customerId: numericSegmentAfter(segments, "customers"),
    };
  }

  if (segments.includes("purchasing")) {
    return { surface: "purchasing", module: "purchasing", route };
  }

  if (segments.includes("production")) {
    return { surface: "production", module: "production", route };
  }

  if (segments.includes("shipping")) {
    return { surface: "shipping", module: "shipping", route };
  }

  if (segments.includes("finance")) {
    return { surface: "finance", module: "finance", route };
  }

  if (segments.includes("reports")) {
    return { surface: "reports", module: "reports", route };
  }

  return {
    surface: moduleKey ? `${moduleKey}-page` : "instance-page",
    module: moduleKey,
    route,
  };
}

function contextLabel(context) {
  if (!context) return "Elite Core";
  if (context.surface === "sales-order" && context.salesOrderId) return `Sales Order #${context.salesOrderId}`;
  if (context.surface === "invoice" && context.invoiceId) return `Invoice #${context.invoiceId}`;
  if (context.surface === "purchase-order" && context.purchaseOrderId) return `PO #${context.purchaseOrderId}`;
  if (context.surface === "project" && context.projectId) return `Project #${context.projectId}`;
  if (context.surface === "customer" && context.customerId) return `Customer #${context.customerId}`;
  if (context.surface === "projects-list") return "Projects";
  if (context.module) {
    return context.module.charAt(0).toUpperCase() + context.module.slice(1);
  }
  return "Elite Core";
}

function usageLabel(usage) {
  if (!usage?.weekly) return null;
  return `${usage.weekly.used} / ${usage.weekly.limit} this week`;
}

export default function GlobalEzeeAssistant({ authState }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [activeView, setActiveView] = useState("chat");
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedbackSubmittingIndex, setFeedbackSubmittingIndex] = useState(null);
  const [feedbackRecorded, setFeedbackRecorded] = useState({});
  const bottomRef = useRef(null);
  const previousInstanceRef = useRef(null);

  const canUseEzee = canUseEzeeAI(authState);
  const instanceUniqueId = useMemo(
    () => getInstanceUniqueId(location.pathname),
    [location.pathname]
  );
  useEffect(() => {
    let active = true;
    setHeaderVisible(false);
    setOpen(false);
    if (!canUseEzee || !instanceUniqueId) return () => { active = false; };
    fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/header-visibility`, {
      headers: { accessToken: localStorage.getItem("accessToken") },
    })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => { if (active) setHeaderVisible(payload?.success === true && payload.visible === true); })
      .catch(() => { if (active) setHeaderVisible(false); });
    return () => { active = false; };
  }, [canUseEzee, instanceUniqueId]);

  const pageContext = useMemo(
    () => buildPageContext(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (!canUseEzee || !instanceUniqueId) {
      setOpen(false);
      return;
    }
  }, [canUseEzee, instanceUniqueId]);

  useEffect(() => {
    if (
      previousInstanceRef.current &&
      previousInstanceRef.current !== instanceUniqueId
    ) {
      setConversationId(null);
      setConversations([]);
      setMessages([]);
      setQuestion("");
      setError(null);
    }
    previousInstanceRef.current = instanceUniqueId;
  }, [instanceUniqueId]);

  const authHeaders = () => ({
    accessToken: localStorage.getItem("accessToken"),
  });

  const loadConversation = async (id) => {
    if (!id || !instanceUniqueId) return;
    setConversationLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain/conversations/${id}`,
        { headers: authHeaders() }
      );
      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || "Unable to load that Ezee AI conversation.");
      }
      setConversationId(Number(payload.conversation?.id || id));
      setFeedbackRecorded({});
      setMessages(
        (payload.messages || []).map((message) => ({
          id: message.id,
          role: message.role,
          content: message.content,
          sources: message.sources || [],
          createdAt: message.createdAt,
        }))
      );
    } catch (err) {
      setError(err.message || "Unable to load that Ezee AI conversation.");
    } finally {
      setConversationLoading(false);
    }
  };

  const loadConversations = async ({ resumeLatest = false } = {}) => {
    if (!instanceUniqueId) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain/conversations?limit=30`,
        { headers: authHeaders() }
      );
      const payload = await response.json();
      if (!response.ok || !payload?.success) return;
      const rows = payload.conversations || [];
      setConversations(rows);
      if (resumeLatest && !conversationId && rows[0]?.id) {
        await loadConversation(rows[0].id);
      }
    } catch (_error) {
      // Conversation history is helpful but should not block asking Ezee.
    }
  };

  useEffect(() => {
    if (!open || !canUseEzee || !instanceUniqueId) return undefined;

    let active = true;
    fetch(
      `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain/usage`,
      { headers: authHeaders() }
    )
      .then((response) => response.json())
      .then((payload) => {
        if (active && payload?.success) setUsage(payload.usage);
      })
      .catch(() => {});

    loadConversations({ resumeLatest: true });

    return () => {
      active = false;
    };
    // Resume the most recent thread only when the drawer opens or instance changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canUseEzee, instanceUniqueId]);

  const startNewChat = () => {
    setConversationId(null);
    setMessages([]);
    setFeedbackRecorded({});
    setQuestion("");
    setError(null);
  };

  const archiveCurrentConversation = async () => {
    if (!conversationId || !instanceUniqueId) return;
    try {
      await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain/conversations/${conversationId}`,
        { method: "DELETE", headers: authHeaders() }
      );
      startNewChat();
      await loadConversations();
    } catch (_error) {
      setError("Unable to archive this Ezee AI conversation.");
    }
  };

  useEffect(() => {
    if (!open) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, loading, open]);

  const ask = async () => {
    const trimmed = String(question || "").trim();
    if (!trimmed || loading || !instanceUniqueId) return;

    setQuestion("");
    setError(null);
    setMessages((previous) => [...previous, { role: "user", content: trimmed }]);
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accessToken: localStorage.getItem("accessToken"),
          },
          body: JSON.stringify({
            question: trimmed,
            conversationId,
            pageContext,
          }),
        }
      );
      const payload = await response.json();

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || "Ezee AI could not answer that question.");
      }

      setUsage(payload.usage || usage);
      if (payload.conversation?.id) {
        setConversationId(Number(payload.conversation.id));
      }
      setMessages((previous) => [
        ...previous,
        {
          role: "assistant",
          content: payload.answer,
          sources: payload.sources || [],
        },
      ]);
      await loadConversations();
    } catch (err) {
      setError(err.message || "Ezee AI could not answer that question.");
    } finally {
      setLoading(false);
    }
  };

  const reportIssue = async (message, index) => {
    if (!instanceUniqueId || feedbackSubmittingIndex !== null) return;
    const reason = window.prompt(
      "What was wrong or what should Ezee have done better?",
      ""
    );
    if (!String(reason || "").trim()) return;

    const previousUser = [...messages.slice(0, index)]
      .reverse()
      .find((item) => item.role === "user");

    setFeedbackSubmittingIndex(index);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/brain/feedback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accessToken: localStorage.getItem("accessToken"),
          },
          body: JSON.stringify({
            conversationId,
            question: previousUser?.content || "",
            answer: message.content || "",
            reason: String(reason).trim(),
            pageContext,
          }),
        }
      );
      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || "Unable to record Ezee feedback.");
      }
      setFeedbackRecorded((current) => ({ ...current, [index]: true }));
    } catch (err) {
      setError(err.message || "Unable to record Ezee feedback.");
    } finally {
      setFeedbackSubmittingIndex(null);
    }
  };

  if (!canUseEzee || !instanceUniqueId || !headerVisible) return null;

  return (
    <>
      <div className="navbar__ezee">
        <Button
          className="navbar__ezee-button"
          size="small"
          variant="outlined"
          startIcon={<AutoAwesomeIcon sx={{ fontSize: 17 }} />}
          onClick={() => setOpen(true)}
          aria-label="Ask Ezee AI"
        >
          Ask Ezee
        </Button>
      </div>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: DRAWER_WIDTH },
            maxWidth: "100vw",
            top: { xs: 0, sm: 80 },
            height: { xs: "100%", sm: "calc(100% - 80px)" },
            borderTopLeftRadius: { xs: 0, sm: 18 },
            boxShadow: "-12px 0 34px rgba(15, 23, 42, 0.12)",
          },
        }}
      >
        <Stack sx={{ height: "100%", minHeight: 0 }}>
          <Box sx={{ px: 2.5, pt: 2.25, pb: 1.5 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Stack direction="row" spacing={1.15} alignItems="center">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    display: "grid",
                    placeItems: "center",
                    color: "#c70909",
                    bgcolor: "#fff1f1",
                  }}
                >
                  <AutoAwesomeIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                    Ezee AI
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Your assistant for Elite Core
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  icon={<LockOutlinedIcon />}
                  label="Role scoped"
                  size="small"
                  variant="outlined"
                  sx={{
                    color: "#b42318",
                    borderColor: "#f4b8b8",
                    bgcolor: "#fff7f7",
                    "& .MuiChip-icon": { color: "inherit" },
                  }}
                />
                <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close Ezee AI">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" mt={1.5}>
              <Chip label={contextLabel(pageContext)} size="small" />
              <Chip
                label="Using current page context"
                size="small"
                variant="outlined"
                sx={{ color: "#087f5b", borderColor: "#a7dfcf", bgcolor: "#f3fcf9" }}
              />
              {usageLabel(usage) ? (
                <Chip label={usageLabel(usage)} size="small" variant="outlined" />
              ) : null}
            </Stack>

            <Stack direction="row" spacing={1} mt={1.25}>
              <Button size="small" variant={activeView==="chat"?"contained":"text"}
                onClick={()=>setActiveView("chat")} sx={{textTransform:"none"}}>
                Chat
              </Button>
              <Button size="small" variant={activeView==="missions"?"contained":"text"}
                onClick={()=>setActiveView("missions")} sx={{textTransform:"none"}}>
                Missions
              </Button>
            </Stack>
            {activeView==="chat"&&(
            <Stack direction="row" spacing={1} alignItems="center" mt={1.5}>
              <TextField
                select
                fullWidth
                size="small"
                label="Conversation"
                value={conversationId || ""}
                onChange={(event) => {
                  const nextId = Number(event.target.value);
                  if (nextId) loadConversation(nextId);
                }}
                disabled={conversationLoading}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <em>New conversation</em>
                </MenuItem>
                {conversations.map((conversation) => (
                  <MenuItem key={conversation.id} value={conversation.id}>
                    {conversation.title || `Conversation #${conversation.id}`}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton
                size="small"
                onClick={startNewChat}
                aria-label="Start new Ezee AI conversation"
                title="New conversation"
              >
                <AddCommentOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={archiveCurrentConversation}
                disabled={!conversationId}
                aria-label="Archive current Ezee AI conversation"
                title="Archive conversation"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
            )}
          </Box>

          <Divider />

          {activeView==="missions"?
            <Box sx={{flex:1,minHeight:0,overflowY:"auto",px:2,py:2}}>
              <MissionWorkspace instanceUniqueId={instanceUniqueId} compact
                onStartChat={prompt=>{setActiveView("chat");if(prompt)setQuestion(prompt);}}/>
            </Box>:
            <>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2.5, py: 2 }}>
            {conversationLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" color="text.secondary" mb={2}>
                <CircularProgress size={17} />
                <Typography variant="body2">Loading conversation…</Typography>
              </Stack>
            ) : null}
            {!messages.length && !conversationLoading ? (
              <Box
                sx={{
                  border: "1px solid #edf0f4",
                  borderRadius: 3,
                  bgcolor: "#fafbfc",
                  p: 2,
                }}
              >
                <Typography fontWeight={800} mb={0.75}>
                  Ask Ezee about this page
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ezee can use your current Elite Core screen as context, check live records,
                  and use only the tools allowed for your Elite Core role. Controlled writes still require their normal confirmation flow.
                </Typography>
              </Box>
            ) : null}

            <Stack spacing={1.5} mt={messages.length ? 0 : 2}>
              {messages.map((message, index) => (
                <Box
                  key={`${message.role}-${index}`}
                  sx={{
                    alignSelf: message.role === "user" ? "flex-end" : "stretch",
                    maxWidth: message.role === "user" ? "88%" : "100%",
                    borderRadius: 3,
                    px: 1.75,
                    py: 1.35,
                    bgcolor: message.role === "user" ? "#fff1f1" : "#f7f8fa",
                    border: "1px solid",
                    borderColor: message.role === "user" ? "#f6d0d0" : "#edf0f4",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={800}
                    sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                  >
                    {message.role === "user" ? "You" : "Ezee AI"}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                    {message.content}
                  </Typography>

                  {message.role === "assistant" ? (
                    <Stack direction="row" spacing={0.75} mt={1.1} alignItems="center">
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<ReportProblemOutlinedIcon sx={{ fontSize: 16 }} />}
                        disabled={feedbackSubmittingIndex === index || feedbackRecorded[index]}
                        onClick={() => reportIssue(message, index)}
                        sx={{ minWidth: 0, px: 0.5, color: "text.secondary", textTransform: "none" }}
                      >
                        {feedbackRecorded[index] ? "Issue recorded" : "Report issue"}
                      </Button>
                    </Stack>
                  ) : null}

                  {!!message.sources?.length && (
                    <Box mt={1.25}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={800}
                        sx={{ display: "block", mb: 0.65 }}
                      >
                        Sources
                      </Typography>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {message.sources.map((source) => (
                          <Chip
                            key={`${source.type}-${source.id}-${source.url}`}
                            label={source.label}
                            size="small"
                            component={source.url ? "a" : "div"}
                            href={source.url || undefined}
                            clickable={Boolean(source.url)}
                            sx={{ textDecoration: "none" }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              ))}

              {loading ? (
                <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                  <CircularProgress size={17} />
                  <Typography variant="body2">Ezee is checking Elite Core…</Typography>
                </Stack>
              ) : null}

              {error ? <Alert severity="error">{error}</Alert> : null}
              <div ref={bottomRef} />
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                maxRows={4}
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    ask();
                  }
                }}
                placeholder="Ask Ezee about this page..."
                size="small"
                disabled={loading}
              />
              <IconButton
                onClick={ask}
                disabled={loading || !question.trim()}
                aria-label="Send to Ezee AI"
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "#c70909",
                  color: "#fff",
                  "&:hover": { bgcolor: "#a80707" },
                  "&.Mui-disabled": { bgcolor: "#f1baba", color: "#fff" },
                }}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "center", mt: 1 }}
            >
              Ezee can make mistakes. Check important information.
            </Typography>
          </Box>
            </>
          }
        </Stack>
      </Drawer>
    </>
  );
}
